import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface Props<T> {
  items: T[];
  itemKey: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  estimatedHeight?: number;
  footer?: React.ReactNode;
  pinnedKey?: string | null;
}

/** Keeps loaded pages in memory while mounting only the visible, measured cards. */
export default function VirtualCardList<T>({ items, itemKey, renderItem, estimatedHeight = 164, footer, pinnedKey }: Props<T>) {
  const viewport = useRef<HTMLDivElement>(null);
  const heights = useRef(new Map<string, number>());
  const [revision, setRevision] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(600);
  const keys = items.map(itemKey);
  const identity = keys[0];
  useLayoutEffect(() => {
    if (viewport.current) viewport.current.scrollTop = 0;
    setScrollTop(0);
  }, [identity]);
  useLayoutEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const measure = () => setViewportHeight(element.clientHeight || 600);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const offsets = useMemo(() => {
    const result = [0];
    for (const key of keys) result.push(result[result.length - 1] + (heights.current.get(key) ?? estimatedHeight));
    return result;
    // Keys change with pages/filters; revision changes when a card is measured.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, estimatedHeight, revision, itemKey]);
  useLayoutEffect(() => {
    const active = new Set(keys);
    heights.current.forEach((_, key) => { if (!active.has(key)) heights.current.delete(key); });
  });
  const measured = useCallback((key: string, height: number) => {
    if (height > 0 && heights.current.get(key) !== height) {
      heights.current.set(key, height);
      setRevision(value => value + 1);
    }
  }, []);
  const total = offsets[offsets.length - 1];
  const top = Math.min(scrollTop, Math.max(0, total - viewportHeight));
  let low = 0;
  let high = items.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (offsets[middle + 1] <= top) low = middle + 1;
    else high = middle;
  }
  const start = Math.max(0, low - 3);
  let end = low;
  while (end < items.length && offsets[end] < top + viewportHeight) end++;
  end = Math.min(items.length, end + 3);
  const indices = Array.from({ length: end - start }, (_, index) => start + index);
  const pinnedIndex = pinnedKey ? keys.indexOf(pinnedKey) : -1;
  if (pinnedIndex >= 0 && (pinnedIndex < start || pinnedIndex >= end)) indices.push(pinnedIndex);
  return <div ref={viewport} data-testid="virtual-card-list" onScroll={event => setScrollTop(event.currentTarget.scrollTop)}
    style={{ overflowY: 'auto', overflowAnchor: 'none', flex: 1, minHeight: 0 }}>
    <div style={{ height: total, position: 'relative' }}>
      {indices.map(index => {
        return <MeasuredCard key={keys[index]} id={keys[index]} top={offsets[index]} measured={measured}>{renderItem(items[index])}</MeasuredCard>;
      })}
    </div>
    {footer}
  </div>;
}

function MeasuredCard({ id, top, measured, children }: { id: string; top: number; measured: (key: string, height: number) => void; children: React.ReactNode }) {
  const element = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = element.current;
    if (!node) return;
    const measure = () => measured(id, node.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [id, measured]);
  return <div ref={element} style={{ position: 'absolute', width: '100%', top, display: 'flow-root' }}>{children}</div>;
}
